import { SESSION_IDLE_MS } from '@l3ns/shared/constants';
import { ulid } from '@l3ns/shared/ulid';
import { buildSession } from './context';
import { st } from './state';

const KEY_FIRST = '_l3ns_f';
const KEY_SESS = '_l3ns_s';

// localStorage can throw (private mode, disabled) — degrade to per-page memory.
const mem = new Map<string, string>();
function lsGet(k: string): string | null {
	try {
		return localStorage.getItem(k);
	} catch {
		return mem.get(k) ?? null;
	}
}
function lsSet(k: string, v: string): void {
	try {
		localStorage.setItem(k, v);
	} catch {
		mem.set(k, v);
	}
}

interface Stored {
	id: string;
	st: number; // startedAt
	la: number; // lastActive
}

/**
 * Ensure a live session (rotating after 30 min idle). Emits nothing itself —
 * returns whether a new session started so the caller can queue
 * session_started / user_returned.
 */
export function ensureSession(): { startedNew: boolean; isReturning: boolean } {
	const now = Date.now();
	let stored: Stored | null = null;
	try {
		const s = JSON.parse(lsGet(KEY_SESS) || '0') as Stored;
		if (typeof s?.id == 'string' && typeof s.st == 'number') stored = s;
	} catch {}
	const first = lsGet(KEY_FIRST);

	if (stored && now - stored.la <= SESSION_IDLE_MS) {
		// live stored session; rebuild context on a fresh page load
		if (st.session_?.id !== stored.id)
			st.session_ = buildSession(
				stored.id,
				stored.st,
				false,
				!!first && Number(first) < stored.st,
				st.rel_,
			);
		lsSet(KEY_SESS, JSON.stringify({ ...stored, la: now }));
		return { startedNew: false, isReturning: st.session_.isReturning };
	}

	// brand-new or idle-expired session
	if (!first) lsSet(KEY_FIRST, String(now));
	const id = ulid();
	st.session_ = buildSession(id, now, true, !!first, st.rel_);
	lsSet(KEY_SESS, JSON.stringify({ id, st: now, la: now } satisfies Stored));
	return { startedNew: true, isReturning: !!first };
}
